import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Input } from '@/common/ui/input'
import { useState } from 'react'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { useQueryClient } from '@tanstack/react-query'
import { PlayerQueryKeys } from '@/modules/manage-player/keys'
import useMutationUpdatePlayerMainWalletBalance from '@/modules/manage-player/api/hooks/useMutationUpdatePlayerMainWalletBalance'

interface PlayerUpdateMainWalletBalanceModalProps {
	player: {
		phone: string
		username?: string
		id: string
		mainWallet: {
			balance: number
		}
	}
}

export function PlayerUpdateMainWalletBalanceModal({
	player,
}: PlayerUpdateMainWalletBalanceModalProps) {
	const [open, setOpen] = useState(false)
	const [newBalance, setNewBalance] = useState('')
	const [confirmText, setConfirmText] = useState('')
	const [note, setNote] = useState('')
	const isConfirmTextValid = confirmText === 'ยืนยัน'
	const isNewBalanceValid =
		!isNaN(Number(newBalance)) && Number(newBalance) >= 0

	const { mutate: updateBalance } = useMutationUpdatePlayerMainWalletBalance()
	const queryClient = useQueryClient()

	const handleConfirm = () => {
		if (!isConfirmTextValid || !isNewBalanceValid) return

		try {
			updateBalance(
				{
					playerId: player.id,
					amount: Number(newBalance),
					...(note.trim() && { note: note.trim() }),
				},
				{
					onSuccess: () => {
						toast.success('อัพเดทยอดเงินในฝากไม่รับโปรเรียบร้อยแล้ว')
						queryClient.invalidateQueries({
							queryKey: [PlayerQueryKeys.GetPlayerDetail, player.id],
						})
						queryClient.invalidateQueries({
							queryKey: [PlayerQueryKeys.GetPlayerList],
						})
						setNewBalance('')
						setConfirmText('')
						setNote('')
						setOpen(false)
					},
					onError: (error) => {
						showErrorToast(error)
					},
				},
			)
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	return (
		<>
			<Button variant="default" size="sm" onClick={() => setOpen(true)}>
				แก้ไขยอดเงิน
			</Button>
			{open && (
				<Dialog open onOpenChange={setOpen}>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>อัพเดทยอดเงินในกระเป๋า</DialogTitle>
							<DialogDescription>
								คุณกำลังจะอัพเดทยอดเงินในกระเป๋าของผู้เล่น
							</DialogDescription>
						</DialogHeader>

						<div className="space-y-4 py-4">
							<div className="space-y-2">
								<p className="text-sm font-medium">ผู้เล่น</p>
								<p className="text-sm text-muted-foreground">
									{player.username || player.phone}
								</p>
							</div>
							<div className="space-y-2">
								<p className="text-sm font-medium">Player ID</p>
								<p className="text-sm text-muted-foreground">{player.id}</p>
							</div>
							<div className="space-y-2">
								<p className="text-sm font-medium">ยอดเงินปัจจุบัน</p>
								<p className="text-sm text-muted-foreground">
									{player.mainWallet.balance?.toLocaleString() || '0'}
								</p>
							</div>
							<div className="space-y-2">
								<p className="text-sm font-medium">ยอดเงินใหม่</p>
								<Input
									type="number"
									value={newBalance}
									onChange={(e) => setNewBalance(e.target.value)}
									placeholder="0"
									min={0}
								/>
							</div>
							<div className="space-y-2">
								<p className="text-sm font-medium">
									หมายเหตุ{' '}
									<span className="font-normal text-muted-foreground">
										(ไม่บังคับ)
									</span>
								</p>
								<Input
									value={note}
									onChange={(e) => setNote(e.target.value)}
									placeholder="ระบุเหตุผลหรือหมายเหตุ"
									maxLength={500}
								/>
							</div>
							<div className="space-y-2">
								<p className="text-sm font-medium">
									กรุณาพิมพ์ &quot;ยืนยัน&quot; เพื่อดำเนินการ
								</p>
								<Input
									value={confirmText}
									onChange={(e) => setConfirmText(e.target.value)}
									placeholder="ยืนยัน"
								/>
							</div>
						</div>

						<DialogFooter>
							<Button variant="outline" onClick={() => setOpen(false)}>
								ยกเลิก
							</Button>
							<Button
								variant="destructive"
								onClick={handleConfirm}
								disabled={!isConfirmTextValid || !isNewBalanceValid}
							>
								ยืนยัน
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			)}
		</>
	)
}
